import React, { useState, useEffect, useRef } from 'react';
import image_upload from '../assets/images/image_upload.svg';
import add_poll from '../assets/images/add_poll.svg';
import quizpic from '../assets/images/quiz.svg';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import loaderIcon from '../assets/images/post_loader.gif';
import config from '../config/config';

const CreatePostPage = () => {
  const [postType, setPostType] = useState('free');
  const userRole = localStorage.getItem('userRole');
  const [media, setMedia] = useState([]);
  const [content, setContent] = useState('');
  const [postPrice, setPostPrice] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('');
  const [mentions, setMentions] = useState([]);
  const [mentionList, setMentionList] = useState([]);
  const [showMentionList, setShowMentionList] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const textareaRef = useRef(null);
  const navigate = useNavigate();

  // Fetch all users once when component mounts
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const response = await axios.get(`${config.baseURL}/get-mentions-creators`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        if (response.data.data) {
          const users = response.data.data.map(item => ({
            id: item.user.id,
            user_name: item.user.user_name,
            name: item.user.name,
            profile: item.user.profile,
          }));
          setAllUsers(users);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchAllUsers();
  }, []);

  const handleBackClick = (event) => {
    if (isSubmitting) {
      event.preventDefault();
      return;
    }
    navigate(-1);
  };

  const handleFileChange = (event) => {
    const filesArray = Array.from(event.target.files);
    setMedia((prevMedia) => [...prevMedia, ...filesArray]);
  };

  const handleContentChange = (event) => {
    const text = event.target.value;
    const cursorPosition = event.target.selectionStart;
    setContent(text);

    const textBeforeCursor = text.slice(0, cursorPosition);
    const atSymbolIndex = textBeforeCursor.lastIndexOf('@');
    
    if (atSymbolIndex !== -1) {
      const searchQuery = textBeforeCursor.slice(atSymbolIndex + 1).toLowerCase();
      if (searchQuery) {
        const filteredUsers = allUsers.filter(user => 
          user.user_name.toLowerCase().startsWith(searchQuery) ||
          user.name.toLowerCase().startsWith(searchQuery)
        );
        setMentionList(filteredUsers);
        setShowMentionList(true);
      } else {
        setShowMentionList(false);
      }
    } else {
      setShowMentionList(false);
    }
  };

  const handleMentionClick = (user) => {
    setMentions((prevMentions) => [...prevMentions, user.id]);
    setShowMentionList(false);

    const textarea = textareaRef.current;
    const cursorPosition = textarea.selectionStart;
    const textBeforeCursor = content.slice(0, cursorPosition);
    const textAfterCursor = content.slice(cursorPosition);
    
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    const newText = textBeforeCursor.slice(0, lastAtIndex) + 
                   `@${user.user_name} ` + 
                   textAfterCursor;
    
    setContent(newText);
    
    // Set cursor position after the inserted mention
    const newCursorPosition = lastAtIndex + user.user_name.length + 2; // +2 for @ and space
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const handlePostTypeChange = (event) => {
    setPostType(event.target.value);
  };

  const handlePriceChange = (event) => {
    const value = event.target.value;
    if (value >= 0) {
      setPostPrice(value);
    } else {
      setPostPrice('');
    }
  };

  const handleRemoveImage = (index) => {
    setMedia((prevMedia) => prevMedia.filter((_, i) => i !== index));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    if (!content.trim() && media.length === 0) {
      setToastMessage('Content or media is required.');
      setToastType('error');
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData();
    formData.append('post_type', postType);
    formData.append('content', content);

    if (postType === 'paid') {
      formData.append('post_price', postPrice);
    }

    media.forEach((file, i) => {
      formData.append(`media[${i}]`, file);
    });

    mentions.forEach((mentionId, i) => {
      formData.append(`mentions[${i}]`, mentionId);
    });

    try {
      const response = await axios.post(`${config.baseURL}/create-post`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.data.success) {
        setToastMessage('Post created successfully!');
        setToastType('success');
        
        setContent('');
        setMedia([]);
        setPostPrice('');
        setMentions([]);
        setShowMentionList(false);
        setMentionList([]);
        
        setTimeout(() => navigate('/home'), 2000);
      } else {
        setToastMessage(response.data.message || 'Unexpected error occurred. Please try again.');
        setToastType('error');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error creating post. Please try again.';
      setToastMessage(errorMessage);
      setToastType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderMentionList = () => {
    return (
      showMentionList && mentionList.length > 0 && (
        <div className="list_arrow_box">
          <ul className="mention-list">
            {mentionList.map((user, index) => (
              <li key={index} onClick={() => handleMentionClick(user)}>
                <div className="imagebox">
                  {user.profile ? (
                    <img src={user.profile} alt={user.user_name} />
                  ) : (
                    <div className="name_box_mentions">
                      {user.name.split(' ').map(n => n[0]).join('')}
                    </div>
                  )}
                </div>
                {user.user_name}
              </li>
            ))}
          </ul>
        </div>
      )
    );
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className="page_title_with_dropdown create_post_page">
          <div className="page_heading">
            <button className="profile_back_btn create_post_btn" onClick={handleBackClick}>
              <svg width={54} height={54} viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M48 27.5363L5.99998 27.5363M5.99998 27.5363L27 49.0532M5.99998 27.5363L27 6.3032" stroke="#222" strokeWidth={6} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <h1>New post</h1>
          </div>
          <div className="create_post_btn">
            <button
              type="submit"
              className={`create_post_btn_area post_btn ${isSubmitting ? 'disabled' : ''}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? <img className="post_loader" src={loaderIcon} alt="Loading..." /> : 'Post'}
            </button>
          </div>
        </div>
        <div className="create_post">
          <textarea
            ref={textareaRef}
            placeholder="Compose new post..."
            value={content}
            onChange={handleContentChange}
          />

          {renderMentionList()}

          <div className="make_post_action">
            <div className="svg_icon_make_post">
              <label htmlFor="file-upload" className={`file-upload-label ${userRole === 'user' ? 'disabled-upload' : ''}`}>
                <img
                  src={image_upload}
                  alt="Upload Media"
                  className={userRole === 'user' ? 'img-disabled' : ''}
                />
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  accept="image/*,video/*"
                  style={{ display: 'none' }}
                />
              </label>
              <div className="post_type_selector">
                <label>
                  <input
                    type="radio"
                    value="free"
                    checked={postType === 'free'}
                    onChange={handlePostTypeChange}
                  />
                  Free
                </label>
                <label>
                  <input
                    type="radio"
                    value="paid"
                    checked={postType === 'paid'}
                    onChange={handlePostTypeChange}
                  />
                  Paid
                </label>
              </div>
              {postType === 'paid' && (
                <input
                  className="form-control"
                  type="number"
                  placeholder="Price"
                  value={postPrice}
                  onChange={handlePriceChange}
                  min="0"
                />
              )}
            </div>
          </div>
          <div className="image-preview">
            {media.map((file, index) => (
              <div className="image-preview-item" key={index}>
                <img className="preview-image" src={URL.createObjectURL(file)} alt={`media-${index}`} />
                <button type="button" className="remove-image-btn" onClick={() => handleRemoveImage(index)}>X</button>
              </div>
            ))}
          </div>

          {userRole === 'user' && (
            <div className="message_for_users_only">
              Only creators can make a post.
            </div>
          )}
        </div>
      </form>

      {toastMessage && (
        <div className={`toast-message ${toastType}`}>
          {toastMessage}
        </div>
      )}
    </>
  );
};

export default CreatePostPage;